import { useState, useEffect } from 'react';
import { ApiConfig } from '../types/fieldTypes';
import { API_BASE_URL } from '../../api/config/axiosConfig';

// Check if we're in development (Vite proxy available) or production
const isDevelopment = import.meta.env.DEV;

// Helper to build full URL for API endpoints
const buildCarApiUrl = (url: string): string => {
  // Already absolute
  if (url.startsWith('http')) return url;
  // Carapi (external proxy) — in dev Vite proxies it; in prod backend proxies it
  if (url.startsWith('/api/carapi')) {
    if (isDevelopment) {
      return url;
    } else {
      return `${API_BASE_URL}${url}`;
    }
  }
  // All relative paths → prepend backend base URL
  return `${API_BASE_URL}${url}`;
};

interface UseApiOptionsResult {
  options: Array<{ value: string; label: string }>;
  loading: boolean;
  error: string | null;
}

interface UseApiOptionsProps {
  apiConfig?: ApiConfig;
  formData?: Record<string, any>;
}

export const useApiOptions = ({
  apiConfig,
  formData = {},
}: UseApiOptionsProps = {}): UseApiOptionsResult => {
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Build dependency array for useEffect
  const dependencyValues = apiConfig?.dependencies
    ? apiConfig.dependencies
        .map(dep => formData?.[dep])
        .filter(val => val !== undefined && val !== null && val !== '')
    : [];

  useEffect(() => {
    // If no API config, return empty options
    if (!apiConfig) {
      setOptions([]);
      return;
    }

    // If dependencies are required but not all are filled, return empty options
    if (apiConfig.dependencies && apiConfig.dependencies.length > 0) {
      const allDependenciesFilled = apiConfig.dependencies.every(
        dep =>
          formData?.[dep] !== undefined &&
          formData?.[dep] !== null &&
          formData?.[dep] !== ''
      );
      if (!allDependenciesFilled) {
        setOptions([]);
        return;
      }
    }

    const fetchOptions = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          url,
          method = 'GET',
          headers,
          params,
          body,
          mapResponse,
        } = apiConfig;

        // Build dynamic params from formData if params is a function
        let finalParams = params;
        if (typeof params === 'function' && formData) {
          finalParams = params(formData);
        }

        // Build full URL (handle carapi endpoints for production)
        let fullUrl = buildCarApiUrl(url);

        // Build URL with query params if provided
        if (finalParams) {
          const queryString = new URLSearchParams(
            Object.entries(finalParams).reduce(
              (acc, [key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                  acc[key] = String(value);
                }
                return acc;
              },
              {} as Record<string, string>
            )
          ).toString();
          if (queryString) {
            fullUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}${queryString}`;
          }
        }

        console.log('🌐 Fetching API options from:', fullUrl);

        // Prepare fetch options
        const fetchOptions: RequestInit = {
          method,
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...headers,
          },
        };

        // Add authorization header if we have a token (for backend API calls)
        const token = localStorage.getItem('authToken');
        if (token && fullUrl.includes(API_BASE_URL)) {
          (fetchOptions.headers as Record<string, string>)['Authorization'] =
            `Bearer ${token}`;
        }

        // Add body for POST requests
        if (method === 'POST' && body) {
          fetchOptions.body = JSON.stringify(body);
        }

        // Fetch data
        const response = await fetch(fullUrl, fetchOptions);

        if (!response.ok) {
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}`
          );
        }

        // Check if response is actually JSON (not HTML error page)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error(
            '❌ Non-JSON response received:',
            text.substring(0, 200)
          );
          throw new Error(
            `Expected JSON but received ${contentType}. This usually means the API endpoint is incorrect or the server returned an error page.`
          );
        }

        const data = await response.json();

        // Map response to options format
        const mappedOptions = mapResponse(data);

        setOptions(mappedOptions);
        setLoading(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch options';
        setError(errorMessage);
        setLoading(false);
        console.error('❌ API Options fetch error:', err);
        console.error('Error details:', errorMessage);
      }
    };

    fetchOptions();
  }, [
    apiConfig?.url,
    apiConfig?.mapResponse,
    ...dependencyValues,
    // Use a stable string representation of formData for dependencies
    apiConfig?.dependencies
      ? JSON.stringify(apiConfig.dependencies.map(dep => formData?.[dep]))
      : '',
  ]);

  return { options, loading, error };
};
